import { cn } from "@newxsystems/shared";
import { DaylightCommonContent } from "../../../../modules/content/components/DaylightCommonContent/DaylightCommonContent";
import { DaylightRichText } from "../../../../modules/content/components/DaylightRichText/DaylightRichText";
import { DaylightSection } from "../../../../modules/content/components/DaylightSection/DaylightSection";
import type { IStatsBlockProps } from "./StatsBlock.props";

const columns = {
  1: "grid-cols-1",
  2: "grid-cols-1 sm:grid-cols-2",
  3: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3",
  4: "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4",
} as const;

/**
 * Rules and gutters belong to the row, not to the first and last item. At three
 * columns the fourth stat opens a second row, and without this it carries the
 * left rule and the indent of a column it is not in, while the row above ends
 * on a rule a third of the width short.
 *
 * Each column count clears the rule and the indent at its own row starts. The
 * ranges are `max-*` scoped because an `nth-child` selector outranks a plain
 * breakpoint utility, so a narrower range's rule would otherwise still be in
 * force once the grid widens.
 */
const edges = {
  1: "",
  2: "sm:border-l sm:px-7 sm:[&:nth-child(2n+1)]:border-l-0 sm:[&:nth-child(2n+1)]:pl-0 sm:[&:nth-child(2n)]:pr-0",
  3: "sm:max-md:border-l sm:max-md:px-7 sm:max-md:[&:nth-child(2n+1)]:border-l-0 sm:max-md:[&:nth-child(2n+1)]:pl-0 sm:max-md:[&:nth-child(2n)]:pr-0 md:border-l md:px-7 md:[&:nth-child(3n+1)]:border-l-0 md:[&:nth-child(3n+1)]:pl-0 md:[&:nth-child(3n)]:pr-0",
  4: "sm:max-md:border-l sm:max-md:px-7 sm:max-md:[&:nth-child(2n+1)]:border-l-0 sm:max-md:[&:nth-child(2n+1)]:pl-0 sm:max-md:[&:nth-child(2n)]:pr-0 md:max-lg:border-l md:max-lg:px-7 md:max-lg:[&:nth-child(3n+1)]:border-l-0 md:max-lg:[&:nth-child(3n+1)]:pl-0 md:max-lg:[&:nth-child(3n)]:pr-0 lg:border-l lg:px-7 lg:[&:nth-child(4n+1)]:border-l-0 lg:[&:nth-child(4n+1)]:pl-0 lg:[&:nth-child(4n)]:pr-0",
} as const;

export const StatsBlock = ({
  alignment,
  anchorId,
  buttons,
  columnCount,
  description,
  eyebrow,
  heading,
  headingEffect,
  items,
  parentClass,
  spacing,
}: IStatsBlockProps) => {
  if (!items?.length) return null;
  const count = columnCount || 3;

  return (
    <DaylightSection className={parentClass} id={anchorId} spacing={spacing}>
      <div className="relative overflow-hidden rounded-[14px] border border-border bg-surface-100 p-7 md:p-12 lg:p-16">
        <div aria-hidden="true" className="signal-dot-grid absolute inset-0 opacity-35" />
        <div className="relative">
          <DaylightCommonContent
            alignment={alignment}
            buttons={buttons}
            description={description}
            eyebrow={eyebrow}
            heading={heading}
            headingEffect={headingEffect}
            reveal
          />
          {/* The closing rule is an inset shadow rather than a border on the grid:
              it spans every column even when the last row is short, and the last
              row's own rule lands on top of it instead of beside it. */}
          <div
            className={cn(
              "mt-12 grid border-t border-border shadow-[inset_0_-1px_0_var(--color-border)] md:mt-16",
              columns[count],
            )}
            data-reveal="stagger"
          >
            {items.map((item, index) => (
              <article
                className={cn("flex flex-col gap-4 border-b border-border py-8", edges[count])}
                key={index}
              >
                {/* The count animates a decorative copy of the value; the real
                    one is read by assistive tech and never changes. */}
                <p className="signal-numeric text-[clamp(2.25rem,4.6vw,3.25rem)] font-semibold leading-none text-primary">
                  <span aria-hidden="true" data-reveal="count" data-reveal-count={item.value}>
                    {item.value}
                  </span>
                  <span className="sr-only">{item.value}</span>
                </p>
                <h3 className="max-w-[24ch] font-daylight-body text-sm font-semibold leading-snug text-foreground">
                  {item.label}
                </h3>
                {item.description ? (
                  <DaylightRichText
                    className="max-w-[32ch] text-base text-ash"
                    content={item.description}
                  />
                ) : null}
              </article>
            ))}
          </div>
        </div>
      </div>
    </DaylightSection>
  );
};

StatsBlock.displayName = "Block::StatsBlock";
